import { useEffect, useRef, useState } from "react";
import { Animated, Platform, Text, View } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Circle, Rect } from "react-native-svg";


/**
 * Branded entrance animation. Renders a full-bleed sand-coloured cover
 * with the Coravu wordmark + targeting-circle mark centred. The cover
 * holds for ~150ms (so the page underneath finishes its first paint),
 * then fades out over 600ms while the wordmark gently scales up. After
 * the animation completes, the cover unmounts entirely so it never
 * interferes with subsequent interactions.
 *
 * Use it once per page load — typically as the first child inside the
 * top-level student route bodies.
 */
export function BrandEntrance() {
  // Once-per-tab-session: only play the entrance the first time the
  // student enters the portal in this browser tab. Subsequent visits
  // (back from a program, navigating around) skip the animation.
  const alreadyPlayed = (() => {
    if (Platform.OS !== "web") return false;
    try {
      return (
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem("coravu_brand_entrance_played") === "1"
      );
    } catch {
      return false;
    }
  })();

  const opacity = useRef(new Animated.Value(alreadyPlayed ? 0 : 1)).current;
  const markScale = useRef(new Animated.Value(alreadyPlayed ? 1 : 0.92)).current;
  const markOpacity = useRef(new Animated.Value(alreadyPlayed ? 0 : 0)).current;
  const [mounted, setMounted] = useState(!alreadyPlayed);

  useEffect(() => {
    if (alreadyPlayed) {
      setMounted(false);
      return;
    }
    Animated.sequence([
      Animated.parallel([
        Animated.timing(markOpacity, {
          toValue: 1,
          duration: 280,
          useNativeDriver: true,
        }),
        Animated.timing(markScale, {
          toValue: 1,
          duration: 480,
          useNativeDriver: true,
        }),
      ]),
      Animated.delay(180),
      Animated.timing(opacity, {
        toValue: 0,
        duration: 520,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setMounted(false);
      try {
        if (typeof window !== "undefined") {
          window.sessionStorage?.setItem("coravu_brand_entrance_played", "1");
        }
      } catch {
        // ignore — animation just plays again next time
      }
    });
  }, [alreadyPlayed, markOpacity, markScale, opacity]);

  if (!mounted) return null;

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        Platform.OS === "web"
          ? ({
              position: "fixed" as unknown as "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.SAND.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            } as object)
          : {
              position: "absolute" as const,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: COLORS.SAND.hex,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
            },
        { opacity },
      ]}
    >
      {/* Soft amber halo behind the mark on web — adds depth. */}
      {Platform.OS === "web" ? (
        <View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: "30%" as unknown as number,
              left: "50%" as unknown as number,
              width: 480,
              height: 480,
              marginLeft: -240,
              marginTop: -180,
              borderRadius: 240,
            },
            {
              backgroundImage:
                `radial-gradient(circle, ${STUDENT_THEME.accentTintSoft} 0%, transparent 60%)`,
            } as object,
          ]}
        />
      ) : null}

      <Animated.View
        style={{
          alignItems: "center",
          opacity: markOpacity,
          transform: [{ scale: markScale }],
        }}
      >
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            backgroundColor: COLORS.FOREST.hex,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 18,
            shadowColor: "#000",
            shadowOpacity: 0.16,
            shadowRadius: 16,
            shadowOffset: { width: 0, height: 6 },
          }}
        >
          <Svg width={40} height={40} viewBox="0 0 34 34" fill="none">
            <Rect width={34} height={34} rx={7} fill={COLORS.FOREST_MID.hex} />
            <Circle cx={17} cy={17} r={11} stroke={COLORS.SAGE.hex} strokeWidth={0.7} />
            <Circle cx={17} cy={17} r={6.5} stroke={COLORS.AMBER.hex} strokeWidth={1.4} />
            <Circle cx={17} cy={17} r={2} fill={COLORS.AMBER.hex} />
          </Svg>
        </View>
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 36,
            letterSpacing: 5,
            color: COLORS.FOREST.hex,
          }}
        >
          COR<Text style={{ color: COLORS.AMBER.hex }}>A</Text>VU
        </Text>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.bodyMedium,
            fontSize: 9,
            letterSpacing: 3,
            color: STUDENT_THEME.textOnSurfaceFaint,
            textTransform: "uppercase",
          }}
        >
          Precision Talent
        </Text>
      </Animated.View>
    </Animated.View>
  );
}

void COLORS.LINEN.hex;
